/**
 * El filtro de certificación MITECO (BB-2000): UN control con dos dimensiones, fichero o periodo.
 *
 * Son dos columnas del contrato (`driveFileName` y `certificationPeriod`), pero para Ops son la misma
 * pregunta a dos niveles de detalle — "¿de qué remesa?" y "¿de qué fichero de esa remesa?" — y no se
 * combinan: filtrar por un periodo Y por un fichero de otro periodo devuelve vacío sin decir por qué.
 * Por eso la UI ofrece una sola lista y un interruptor que decide qué contiene, en vez de dos
 * desplegables independientes que invitan a ese cruce. Pedido por Javi el 2026-09-10: "una nueva
 * opción para filtrar: por fichero o por periodo".
 *
 * Cambiar de dimensión VACÍA los valores. Es la regla que hace que el control sea honesto: si se
 * conservaran, un fichero elegido seguiría viajando en `criterios` mientras la pantalla enseña la
 * lista de periodos, y lo que se ve dejaría de ser lo que se filtra.
 */
import type { Criterios } from '@caes-payments/shared';

import type { Vocabulario } from './vocabulario';

export type DimensionCertificacion = 'fichero' | 'periodo';

export interface FiltroCertificacion {
  dimension: DimensionCertificacion;
  valores: string[];
}

export const FILTRO_CERTIFICACION_VACIO: FiltroCertificacion = { dimension: 'periodo', valores: [] };

/** Cambia la dimensión. Si es otra, los valores se vacían; si es la misma, no se toca nada. */
export function cambiarDimension(
  filtro: FiltroCertificacion,
  dimension: DimensionCertificacion,
): FiltroCertificacion {
  if (dimension === filtro.dimension) return filtro;
  return { dimension, valores: [] };
}

/**
 * Vuelca el filtro en los criterios, en el campo del contrato que corresponde a la dimensión activa.
 * Con la lista vacía no escribe nada: vacío se OMITE, igual que los otros filtros ("todos").
 */
export function aplicarCertificacion(criterios: Criterios, filtro: FiltroCertificacion): Criterios {
  if (filtro.valores.length === 0) return criterios;
  return filtro.dimension === 'fichero'
    ? { ...criterios, driveFileName: filtro.valores }
    : { ...criterios, certificationPeriod: filtro.valores };
}

/** Las opciones del desplegable según la dimensión activa. */
export function opcionesDeCertificacion(
  vocabulario: Pick<Vocabulario, 'ficheros' | 'periodos'>,
  dimension: DimensionCertificacion,
): string[] {
  return dimension === 'fichero' ? vocabulario.ficheros : vocabulario.periodos;
}

/** Cómo se nombra cada dimensión en la UI. Un solo sitio, para el interruptor y para los textos. */
export const ETIQUETA_DIMENSION: Record<DimensionCertificacion, string> = {
  periodo: 'Periodo',
  fichero: 'Fichero',
};
